import AppHeader from '../components/AppHeader';
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { ArrowBack, Save, Email, Delete } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { settingsAPI, protocolAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  const [smtpSettings, setSmtpSettings] = useState({
    smtpServer: '',
    smtpPort: '587',
    smtpUser: '',
    smtpPassword: '',
    senderEmail: 'info@split-klimaanlagen.com',
    senderName: 'Engelhardt Wartungsservice'
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const result = await settingsAPI.getSMTP();
      if (result.success && result.settings) {
        setSmtpSettings(prev => ({ ...prev, ...result.settings }));
      }
    } catch (err) {
      console.error('Error loading settings:', err);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const result = await settingsAPI.saveSMTP(smtpSettings);
      if (result.success) {
        setSuccess('Einstellungen gespeichert');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  const handleTestSMTP = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const result = await settingsAPI.testSMTP(smtpSettings);
      if (result.success) {
        setSuccess('SMTP-Verbindung erfolgreich getestet');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'SMTP-Test fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllProtocols = async () => {
    if (!window.confirm('Möchten Sie wirklich ALLE Protokolle löschen?')) {
      return;
    }
    
    setLoading(true);
    try {
      const result = await protocolAPI.deleteAll();
      setSuccess(`${result.deleted_count} Protokoll(e) gelöscht`);
    } catch (err) {
      setError('Fehler beim Löschen der Protokolle');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppHeader />

      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            <Email sx={{ mr: 1, verticalAlign: 'middle' }} />
            E-Mail Einstellungen (SMTP)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Konfigurieren Sie die SMTP-Einstellungen für den E-Mail-Versand
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="SMTP Server"
              value={smtpSettings.smtpServer}
              onChange={(e) => setSmtpSettings({ ...smtpSettings, smtpServer: e.target.value })}
              placeholder="smtp.gmail.com"
            />
            <TextField
              fullWidth
              label="SMTP Port"
              value={smtpSettings.smtpPort}
              onChange={(e) => setSmtpSettings({ ...smtpSettings, smtpPort: e.target.value })}
              placeholder="587"
            />
            <TextField
              fullWidth
              label="Benutzername"
              value={smtpSettings.smtpUser}
              onChange={(e) => setSmtpSettings({ ...smtpSettings, smtpUser: e.target.value })}
            />
            <TextField
              fullWidth
              type="password"
              label="Passwort"
              value={smtpSettings.smtpPassword}
              onChange={(e) => setSmtpSettings({ ...smtpSettings, smtpPassword: e.target.value })}
            />
            <TextField
              fullWidth
              label="Absender E-Mail"
              value={smtpSettings.senderEmail}
              onChange={(e) => setSmtpSettings({ ...smtpSettings, senderEmail: e.target.value })}
            />
            <TextField
              fullWidth
              label="Absender Name"
              value={smtpSettings.senderName}
              onChange={(e) => setSmtpSettings({ ...smtpSettings, senderName: e.target.value })}
            />

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSaveSettings}
                disabled={loading}
              >
                Speichern
              </Button>
              <Button
                variant="outlined"
                onClick={handleTestSMTP}
                disabled={loading}
              >
                Verbindung testen
              </Button>
            </Box>
          </Box>
        </Paper>

        {user?.role === 'admin' && (
          <Paper sx={{ p: 3, bgcolor: '#fff3cd' }}>
            <Typography variant="h6" gutterBottom color="error">
              <Delete sx={{ mr: 1, verticalAlign: 'middle' }} />
              Gefährliche Aktionen
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Diese Aktionen können nicht rückgängig gemacht werden!
            </Typography>
            <Button
              variant="contained"
              color="error"
              startIcon={<Delete />}
              onClick={handleDeleteAllProtocols}
              disabled={loading}
            >
              Alle Protokolle löschen
            </Button>
          </Paper>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress />
          </Box>
        )}
      </Container>
    </Box>
  );
}